/**
 * Match-clock lifecycle state machine (PMA-COCK-144).
 *
 * Client-reported bug: while a SCOPED stoppage is open — a VAR review or a
 * timeout, each with its own dedicated start/stop control (`btn-var-toggle`,
 * `btn-timeout-toggle`) — the GLOBAL "Reanudar Tiempo Efectivo" resume
 * control stayed clickable. Pressing it instead of the scoped stoppage's own
 * end control flips the match back to `EFFECTIVE` while the VAR/timeout
 * stoppage is still open server-side (its own Stop event was never sent),
 * corrupting the ineffective-time accounting and the timer sequence for the
 * rest of the match.
 *
 * The client explicitly asked for a real state machine — not conditional
 * `disabled` juggling in the render — so illegal transitions are rejected
 * structurally, in one place, independent of any particular render. This
 * module is that machine: a pure derivation + pure reducer, with zero React
 * dependency, so every transition can be exhaustively unit-tested without
 * mounting anything.
 *
 * PHASES
 * ------
 * - `RUNNING`         — the ball is in play (`clockMode === "EFFECTIVE"`).
 * - `STOPPED_GENERIC`  — an ineffective stoppage is open (`clockMode ===
 *   "INEFFECTIVE"`) with no VAR/timeout tag on it — a plain team-attributed
 *   or neutral-referee stoppage (throw-in, injury, foul, ...). The global
 *   resume control is the CORRECT, only way to end this kind of stoppage —
 *   there is no separate per-stoppage "Finalizar" button for it.
 * - `STOPPED_SCOPED`   — a VAR review and/or a timeout is open. Each scoped
 *   stoppage has its OWN end control (`btn-var-toggle`/`btn-timeout-toggle`,
 *   labelled "Finalizar ..." when active) — the global resume control MUST
 *   be rejected here; the fix this module exists to deliver.
 *
 * A scoped stoppage always implies `STOPPED_SCOPED` regardless of what
 * `clockMode` currently reads — VAR/timeout can legally start while
 * `clockMode` is still `EFFECTIVE` (PMA-COCK-126 AC3: "clock genuinely
 * paused" case), and the resume control must stay non-actionable in that
 * state too, defensively, even though it is not rendered there today (see
 * `deriveClockState`'s doc comment).
 *
 * WHAT THIS MODULE DOES **NOT** GOVERN
 * -------------------------------------
 * `OPEN_SCOPED_STOPPAGE`/`CLOSE_SCOPED_STOPPAGE` below are pure bookkeeping
 * folds, not a second legality gate for starting/stopping VAR or a timeout.
 * That legality already lives, correctly and independently tested, in
 * `useCockpitClockHandlers.ts`'s `neutralTimerStartBlocked` (PMA-COCK-126) —
 * which permits an EFFECTIVE-but-paused VAR/timeout start this module's
 * `RUNNING`/`STOPPED_GENERIC` split cannot see (it only knows `clockMode`,
 * not `isGlobalClockRunning`). Never gate a VAR/timeout START on this
 * module's phase — only on the existing PMA-COCK-126 guard. This module owns
 * exactly one new rule: the global RESUME transition is illegal while any
 * scoped stoppage is open.
 */

export type ScopedStoppageKind = "VAR" | "TIMEOUT";

export type ClockPhase = "RUNNING" | "STOPPED_GENERIC" | "STOPPED_SCOPED";

export interface ClockMachineState {
  phase: ClockPhase;
  /** Non-empty iff `phase === "STOPPED_SCOPED"`; empty otherwise. */
  scopedKinds: ScopedStoppageKind[];
}

export const RUNNING_STATE: ClockMachineState = {
  phase: "RUNNING",
  scopedKinds: [],
};

const STOPPED_GENERIC_STATE: ClockMachineState = {
  phase: "STOPPED_GENERIC",
  scopedKinds: [],
};

/**
 * Derive the current clock-lifecycle phase from the same signals
 * `CockpitContext.tsx` already computes: `clockMode` (from `useMatchTimer`),
 * and `isVarActive`/`isTimeoutActive` (the broadcast-/breakdown-derived
 * flags — NOT the local-only `isVarActiveLocal` used elsewhere for the
 * Detener-while-VAR sequence, which does not survive a page reload).
 *
 * A scoped stoppage takes priority over `clockMode`: if either is active,
 * the phase is always `STOPPED_SCOPED`, even in the (today UI-unreachable
 * for the resume control specifically, since it isn't rendered while
 * `clockMode === "EFFECTIVE"`) edge case where VAR/timeout started while the
 * global clock was merely paused rather than switched to `INEFFECTIVE`. This
 * is deliberate defense in depth — the guard must reject even if a resume
 * click somehow arrives outside the render path this derivation is normally
 * read from.
 */
export const deriveClockState = (input: {
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isVarActive: boolean;
  isTimeoutActive: boolean;
}): ClockMachineState => {
  const scopedKinds: ScopedStoppageKind[] = [];
  if (input.isVarActive) scopedKinds.push("VAR");
  if (input.isTimeoutActive) scopedKinds.push("TIMEOUT");
  if (scopedKinds.length > 0) return { phase: "STOPPED_SCOPED", scopedKinds };
  if (input.clockMode === "INEFFECTIVE") return STOPPED_GENERIC_STATE;
  return RUNNING_STATE;
};

export type ClockMachineAction =
  | { type: "BEGIN_GENERIC_STOPPAGE" }
  | { type: "OPEN_SCOPED_STOPPAGE"; kind: ScopedStoppageKind }
  | { type: "CLOSE_SCOPED_STOPPAGE"; kind: ScopedStoppageKind }
  | { type: "RESUME_EFFECTIVE" };

export type ClockRejectionReason =
  /** RESUME_EFFECTIVE from RUNNING: the clock is already running. */
  | "already-running"
  /** BEGIN_GENERIC_STOPPAGE from a non-RUNNING phase: already stopped. */
  | "already-stopped"
  /**
   * RESUME_EFFECTIVE from STOPPED_SCOPED — THE fix this module exists to
   * deliver. The operator must close the open VAR/timeout stoppage via its
   * own control first.
   */
  | "scoped-stoppage-open"
  /** CLOSE_SCOPED_STOPPAGE for a kind that isn't currently open. */
  | "no-such-scoped-stoppage";

export type ClockTransitionResult =
  | { allowed: true; state: ClockMachineState }
  | { allowed: false; reason: ClockRejectionReason; state: ClockMachineState };

/**
 * Pure reducer: given the current state and an attempted action, returns
 * either the resulting state (legal) or a typed rejection reason (illegal),
 * always alongside the state unchanged in the rejection case so a caller
 * never has to special-case "what state are we still in."
 */
export const applyClockMachineAction = (
  state: ClockMachineState,
  action: ClockMachineAction,
): ClockTransitionResult => {
  switch (action.type) {
    case "RESUME_EFFECTIVE": {
      if (state.phase === "RUNNING") {
        return { allowed: false, reason: "already-running", state };
      }
      if (state.phase === "STOPPED_SCOPED") {
        return { allowed: false, reason: "scoped-stoppage-open", state };
      }
      return { allowed: true, state: RUNNING_STATE };
    }

    case "BEGIN_GENERIC_STOPPAGE": {
      if (state.phase !== "RUNNING") {
        return { allowed: false, reason: "already-stopped", state };
      }
      return { allowed: true, state: STOPPED_GENERIC_STATE };
    }

    case "OPEN_SCOPED_STOPPAGE": {
      // Bookkeeping only — see the module doc comment. Never used as a
      // second gate ahead of PMA-COCK-126's neutralTimerStartBlocked.
      if (state.scopedKinds.includes(action.kind)) {
        // Toggling an already-open kind is a close, not an open.
        return { allowed: false, reason: "already-stopped", state };
      }
      return {
        allowed: true,
        state: {
          phase: "STOPPED_SCOPED",
          scopedKinds: [...state.scopedKinds, action.kind],
        },
      };
    }

    case "CLOSE_SCOPED_STOPPAGE": {
      if (!state.scopedKinds.includes(action.kind)) {
        return { allowed: false, reason: "no-such-scoped-stoppage", state };
      }
      const remaining = state.scopedKinds.filter((k) => k !== action.kind);
      return {
        allowed: true,
        state:
          remaining.length > 0
            ? { phase: "STOPPED_SCOPED", scopedKinds: remaining }
            : STOPPED_GENERIC_STATE,
      };
    }

    default: {
      // Exhaustiveness guard — a new ClockMachineAction variant added
      // without a matching case fails to compile here.
      const _exhaustive: never = action;
      return { allowed: false, reason: "already-stopped", state: _exhaustive };
    }
  }
};

/**
 * Convenience wrapper for call sites that only need the yes/no answer for
 * "can the global resume control fire right now" — the exact question
 * `MatchTimerDisplay.tsx` and `LoggerView.tsx`'s two resume-button render
 * sites need for their `disabled`/`title` props, without each duplicating
 * `deriveClockState` + `applyClockMachineAction` wiring by hand.
 */
export const canResumeEffective = (input: {
  clockMode: "EFFECTIVE" | "INEFFECTIVE";
  isVarActive: boolean;
  isTimeoutActive: boolean;
}): boolean =>
  applyClockMachineAction(deriveClockState(input), {
    type: "RESUME_EFFECTIVE",
  }).allowed;
